/**
 * Probe thresholds + alias resolution for the Apex dashboard.
 *
 * Canonical probe IDs and the Apex display-name aliases the dashboard
 * recognises (case-insensitive):
 *
 *   tmp  ← "Tmp", "Temp", "Temperature"
 *   pH   ← "pH", "PH"
 *   orp  ← "ORP", "Orp"
 *   alk  ← "Alk", "ALK", "Alkalinity"
 *   ca   ← "Ca", "CAL", "Cal", "Calcium"
 *   mg   ← "Mg", "MAG", "Mag", "Magnesium"
 *
 * To add a new canonical probe (Salt, PO4, NO3, …):
 *   1. Add an entry to {@link PROBE_BANDS} with green/yellow tuples.
 *   2. Add its aliases to {@link PROBE_ALIASES}.
 *   3. Add it to {@link CRITICAL_PROBE_ORDER} if it belongs in the top row.
 *   4. Update the alias table comment above.
 *
 * Thresholds intentionally live in this single file so a non-engineer
 * tank operator can grep for a number and edit it. They are NOT pulled
 * from the agent because they describe display intent, not control logic.
 */
import type { ApexProbeSnapshot } from './types';

export type ProbeId = 'tmp' | 'pH' | 'orp' | 'alk' | 'ca' | 'mg';
export type ProbeStatus = 'ok' | 'warn' | 'danger';

/**
 * Inclusive band edges for each canonical probe.
 *
 *   - `green`  : value is comfortably in range
 *   - `yellow` : value is in a warning band straddling the green edges
 *   - anything outside the union of green ∪ yellow is `danger`
 *
 * Both tuples are stored as `[min, max]` with `min <= max`.
 */
export interface ProbeBands {
  green: [number, number];
  yellow: [number, number];
  /** Display label shown next to the value (e.g. "°F"). May be empty. */
  unitLabel: string;
}

export const PROBE_BANDS: Record<ProbeId, ProbeBands> = {
  tmp: { green: [76, 80], yellow: [75, 82], unitLabel: '°F' },
  pH: { green: [7.9, 8.4], yellow: [7.8, 8.5], unitLabel: '' },
  orp: { green: [250, 450], yellow: [200, 500], unitLabel: 'mV' },
  alk: { green: [7.5, 10.5], yellow: [7.0, 11.0], unitLabel: 'dKH' },
  ca: { green: [400, 460], yellow: [380, 480], unitLabel: 'ppm' },
  mg: { green: [1250, 1400], yellow: [1200, 1450], unitLabel: 'ppm' },
};

/**
 * Case-insensitive alias map. Each canonical id lists every probe name we'll
 * accept from the Apex; the resolver lowercases on both sides before matching.
 */
const PROBE_ALIASES: Record<ProbeId, string[]> = {
  tmp: ['tmp', 'temp', 'temperature'],
  pH: ['ph'],
  orp: ['orp'],
  alk: ['alk', 'alkalinity'],
  ca: ['ca', 'cal', 'calcium'],
  mg: ['mg', 'mag', 'magnesium'],
};

/** Display order for the "Critical Probes" row at the top of the dashboard. */
export const CRITICAL_PROBE_ORDER: ProbeId[] = ['tmp', 'pH', 'orp', 'alk', 'ca', 'mg'];

/** Friendly label printed under the big numeric value. */
export const PROBE_LABELS: Record<ProbeId, string> = {
  tmp: 'Temperature',
  pH: 'pH',
  orp: 'ORP',
  alk: 'Alkalinity',
  ca: 'Calcium',
  mg: 'Magnesium',
};

const ALIAS_LOOKUP: Map<string, ProbeId> = (() => {
  const m = new Map<string, ProbeId>();
  for (const id of Object.keys(PROBE_ALIASES) as ProbeId[]) {
    for (const alias of PROBE_ALIASES[id]) m.set(alias.toLowerCase(), id);
  }
  return m;
})();

/**
 * Map an Apex probe display name to a canonical {@link ProbeId}, or `null`
 * when no alias matches. Comparison is case-insensitive and trims surrounding
 * whitespace; everything else is exact.
 */
export function resolveProbeId(name: string): ProbeId | null {
  return ALIAS_LOOKUP.get(name.trim().toLowerCase()) ?? null;
}

/**
 * Bucket a numeric value into ok / warn / danger using the probe's band
 * definitions. `value` must be finite — NaN returns `'danger'` so the UI
 * can't silently ignore a broken probe read.
 */
export function classifyProbe(id: ProbeId, value: number): ProbeStatus {
  if (!Number.isFinite(value)) return 'danger';
  const bands = PROBE_BANDS[id];
  const [gLo, gHi] = bands.green;
  const [yLo, yHi] = bands.yellow;
  if (value >= gLo && value <= gHi) return 'ok';
  if (value >= yLo && value <= yHi) return 'warn';
  return 'danger';
}

/**
 * Convenience: resolve + classify in a single call. Returns `null` if the
 * probe name doesn't match any known canonical id.
 */
export function classifyApexProbe(
  probe: Pick<ApexProbeSnapshot, 'name' | 'value'>,
): { id: ProbeId; status: ProbeStatus } | null {
  const id = resolveProbeId(probe.name);
  if (!id) return null;
  return { id, status: classifyProbe(id, probe.value) };
}